import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { ArrowLeft, Coins, Gift, Trophy, Medal, Broadcast } from "@phosphor-icons/react";

export default function Backpack() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/me/backpack")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load your Backpack."));
  }, []);

  if (err) {
    return (
      <div className="min-h-[100dvh] bg-ink flex flex-col items-center justify-center gap-4 text-cream/60 px-6 text-center" data-testid="backpack-error">
        <p>{err}</p>
        <button onClick={() => nav("/profile")} className="px-5 py-2.5 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.2em] uppercase">Back to profile</button>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-[100dvh] flex items-center justify-center text-cream/40 bg-ink">Loading your Backpack…</div>;
  }

  const gifts = data.gifts?.items || [];
  const titles = data.titles || [];
  const milestones = data.badges?.milestones || [];
  const allBadges = data.badges?.all || [];
  const league = data.league || {};

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="backpack-page">
      <header className="sticky top-0 z-30 crystal-glass px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="backpack-back" className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Rewards</p>
          <h1 className="text-cream text-2xl font-black tracking-tighter">Backpack</h1>
        </div>
        <span className="text-2xl">🎒</span>
      </header>

      <div className="px-5 mt-4 space-y-6">
        {/* Balances */}
        <div className="grid grid-cols-2 gap-3" data-testid="backpack-balances">
          <div className="rounded-2xl bg-gradient-to-br from-papaya/25 to-ink border border-papaya/30 p-4">
            <Coins size={20} weight="duotone" className="text-papaya" />
            <p className="text-cream font-black text-2xl tracking-tighter mt-2">{(data.wave_balance || 0).toLocaleString()}</p>
            <p className="text-cream/50 text-[10px] tracking-[0.15em] uppercase font-bold">Waves</p>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-pink-500/20 to-ink border border-pink-400/30 p-4">
            <Gift size={20} weight="duotone" className="text-pink-300" />
            <p className="text-cream font-black text-2xl tracking-tighter mt-2">{(data.pearl_balance || 0).toLocaleString()}</p>
            <p className="text-cream/50 text-[10px] tracking-[0.15em] uppercase font-bold">Pearls · lifetime {(data.total_pearls_earned || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* League */}
        <div className="rounded-3xl bg-white/5 border border-white/5 p-4 flex items-center gap-3" data-testid="backpack-league">
          <div className="w-12 h-12 rounded-2xl bg-amber-400/15 flex items-center justify-center">
            <Broadcast size={22} weight="duotone" className="text-amber-300" />
          </div>
          <div className="flex-1">
            <p className="text-cream font-black tracking-tight">{league.current?.label || "Unranked"}</p>
            <p className="text-cream/50 text-xs">LIVE league · {(league.rewards_waves || 0).toLocaleString()} 🪙 rewards earned</p>
          </div>
        </div>

        {/* Titles */}
        <section data-testid="backpack-titles">
          <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Titles</p>
          {titles.length === 0 ? (
            <p className="text-cream/40 text-sm">No titles yet — win a LIVE PK Battle to earn the PK Champion title.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {titles.map((t) => (
                <div key={t.key} className="flex items-center gap-2 px-3 py-2 rounded-full bg-papaya/15 border border-papaya/40 text-papaya text-sm font-bold">
                  <span>{t.emoji}</span> {t.label} <span className="text-papaya/70 text-xs">×{t.count}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Gifts inventory */}
        <section data-testid="backpack-gifts">
          <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">
            Gifts received {data.gifts?.total ? `· ${data.gifts.total.toLocaleString()}` : ""}
          </p>
          {gifts.length === 0 ? (
            <p className="text-cream/40 text-sm">No gifts received yet. Go LIVE and let your fans shower you.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {gifts.map((gg, i) => (
                <div key={i} className="rounded-2xl bg-white/5 border border-white/5 p-3 flex flex-col items-center text-center">
                  {gg.image_url ? (
                    <img src={fileUrl(gg.image_url)} alt={gg.name} className="w-10 h-10 object-contain" />
                  ) : (
                    <span className="text-3xl leading-none">{gg.emoji || "🎁"}</span>
                  )}
                  <p className="text-cream text-[11px] font-bold mt-2 truncate w-full">{gg.name}</p>
                  <p className="text-papaya text-xs font-black">×{gg.quantity}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Badges */}
        <section data-testid="backpack-badges">
          <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">
            Badges {milestones.length ? `· ${milestones.length} earned` : ""}
          </p>
          <div className="grid grid-cols-4 gap-2">
            {allBadges.map((b, i) => (
              <div
                key={i}
                className={`rounded-2xl border p-3 flex flex-col items-center text-center transition-opacity ${b.earned ? "bg-amber-400/10 border-amber-400/30" : "bg-white/5 border-white/5 opacity-40"}`}
              >
                <span className="text-2xl leading-none">{b.emoji || "🏅"}</span>
                <p className="text-cream text-[10px] font-bold mt-1.5 leading-tight">{b.label}</p>
                {!b.earned && <p className="text-cream/40 text-[9px] mt-0.5">{(b.pearls || 0).toLocaleString()} Pearls</p>}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
